<template>
  <master-layout pageTitle="Live Chat">
    <!-- Mobile layout -->
    <div v-if="isMobile" class="page-inbox-container">
      <div class="inbox">
        <div v-if="active === 'home'" class="col-full">
          <live-chat-home @addEditModal="addEditModal = true" :active="activeChatId" @customer="changeActiveChat" @onFilterCustomers="goToHome" @onClearFilter="goToHome" />
        </div>
        <div v-else class="col-full">
          <single-customer-chats @home="goToHome" :conversationid="activeChatId" @resolved_chat_init="activeChatId = undefined" />
        </div>
      </div>
    </div>
    <!-- Desktop layout -->
    <div v-else class="page-inbox-container">
      <div class="inbox">
        <div class="col-home">
          <live-chat-home @addEditModal="addEditModal = true" :active="activeChatId" @customer="changeActiveChat" @onFilterCustomers="goToHome" @onClearFilter="goToHome" />
        </div>
        <div class="col-chat">
          <single-customer-chats @home="goToHome" :conversationid="activeChatId" @resolved_chat_init="activeChatId = undefined" />
        </div>
      </div>
    </div>

    <!-- New Conversation Modal -->
    <VDialog
      v-if="addEditModal || newChat.channel"
      :open="addEditModal || newChat.channel !== undefined"
      headline="Start a new Conversation"
      modal
      full-width-body
      @close="() => { addEditModal = false; newChat.channel = undefined; }"
      style="--dialog-min-inline-size: 450px; --dialog-max-inline-size: 450px;"
    >
      <template #body>
        <div v-if="addEditModal" class="channel_container">
          <p class="sub-title" style="margin: 0 0 10px 0;">Select Channel</p>
          <div class="start-conversation">
            <div
              v-for="(thisChannel, cIndex) in socialPlatformConnectedArray"
              :key="cIndex"
              class="channel-item"
              :class="{ selected: newChat.channel === thisChannel }"
              @click="newChat.channel = thisChannel"
            >
              <div v-if="channels[thisChannel]" class="label">
                <img :src="channels[thisChannel].icon" class="channel-icon-img" :alt="thisChannel" />
                <span class="title">{{ channels[thisChannel].name }}</span>
              </div>
              <div v-else class="label">{{ thisChannel }}</div>
              <div class="radio-indicator" :class="{ active: newChat.channel === thisChannel }"></div>
            </div>
          </div>
        </div>
        <div v-else class="channel_container">
          <div class="prdct-section">
            <p class="sub-title paddingBottom" style="margin: 0;">Mobile Number <span class="important">*</span></p>
            <mobile-input v-model:countryCode="newChat.countryCode" v-model:mobile="newChat.mobilenumber" maxlength='15'></mobile-input>
          </div>
          <div class="prdct-section">
            <p class="sub-title paddingBottom" style="margin: 0;">First Name</p>
            <VTextField type="text" placeholder="Please enter the First Name" :maxlength="60" aria-label="Enter First Name" v-model="newChat.firstName" appearance="ghost" class="vivid-input"></VTextField>
          </div>
          <div class="prdct-section">
            <p class="sub-title paddingBottom" style="margin: 0;">Last Name</p>
            <VTextField type="text" placeholder="Please enter the Last Name" :maxlength="60" aria-label="Enter Last Name" v-model="newChat.lastName" appearance="ghost" class="vivid-input"></VTextField>
          </div>
        </div>
      </template>
      <template #action-items>
        <div v-if="addEditModal" class="footer">
          <VButton class="vvd-btn-cancel flex100P" appearance="filled" label="Close" @click="() => { addEditModal = false; newChat.channel = undefined; }"></VButton>
          <VButton class="vvd-btn-primary flex100P" connotation="cta" appearance="filled" label="Next" :disabled="!newChat.channel" @click="() => addEditModal = false"></VButton>
        </div>
        <div v-else class="footer">
          <VButton class="vvd-btn-cancel flex100P" appearance="filled" label="Previous" @click="() => addEditModal = true"></VButton>
          <VButton class="vvd-btn-primary flex100P" connotation="cta" appearance="filled" :label="!commonLoader ? 'Create' : ''" :disabled="isDisabledNewConv" @click="startNewConv">
            <template v-if="commonLoader" #icon>
              <VProgressRing class="vvd-spinner vvd-spinner-sm" connotation="accent" indeterminate />
            </template>
          </VButton>
        </div>
      </template>
    </VDialog>
  </master-layout>
</template>

<script>

import LiveChatHome from "./LiveChatHome.vue";
import SingleCustomerChats from "./SingleCustomerChats.vue";
import apiInterceptor from '@/shared/apiInterceptor';
import MobileInput from '../components/mobileInput.vue';
import { channels } from "@/resources/js/bot-builder";
import { useUIStore } from '@/stores/ui';
import { useSocialChannels } from '@/composables/useSocialChannels';
import { environment } from '@/shared/env';

const newChat = {
  channel: undefined,
  countryCode: undefined,
  mobilenumber: "",
  firstName: undefined,
  lastName: undefined,
  pageid: undefined
}

export default {
  components: { LiveChatHome, SingleCustomerChats, MobileInput },
  setup() {
    const { whatsappConnected, smsConnected, googlercsConnected, googlercsSocialData, whatsAppSocialData, smsSocialData, socialPlatformConnectedArray } = useSocialChannels();
    return { whatsappConnected, smsConnected, googlercsConnected, googlercsSocialData, whatsAppSocialData, smsSocialData, socialPlatformConnectedArray };
  },
  data() {
    return {
      active: "home",
      activeChatId: undefined,
      addEditModal: false,
      commonLoader: false,
      isMobile: window?.innerWidth && window.innerWidth > 1200 ? false : true,
      newChat: JSON.parse(JSON.stringify(newChat)),
      channels
    };
  },
  created() {
    // Use `created` (not `beforeCreate`): component methods and this.$route are
    // not yet available in beforeCreate, so calling getSanitizedConversationId()
    // there throws "is not a function".
    this.activeChatId = this.getSanitizedConversationId();
  },
  mounted() {
    window.addEventListener("resize", this.onresize);
    this.activeChatId = this.getSanitizedConversationId();
    const event = new CustomEvent("openedLiveChat");
    document.dispatchEvent(event);
  },
  computed: {
    isDisabledNewConv() {
      if(this.addEditModal) {
        if(this.newChat.channel) return false;
        return true;
      }
      const { mobilenumber, countryCode } = this.newChat;
      if (!mobilenumber || !countryCode || mobilenumber.length <= 7 || this.commonLoader) {
        return true;
      }
      return false;
    }
  },
  watch: {
    '$route.query.conversation'() {
      this.activeChatId = this.getSanitizedConversationId();
    },
    activeChatId() {
      if (this.activeChatId) {
        this.active = "chat";
        this.$router.replace({ path: '/page-inbox', query: { conversation: this.activeChatId } });
      } else if(this.$route.path === '/page-inbox') {
        this.active = "home";
        this.$router.replace({ path: '/page-inbox', query: null });
      }
    },
  },
  methods: {
    // Reads the conversation id from the route query, treating the literal
    // strings "undefined"/"null" as absent so they never reach a customer
    // URL as crm/v1/customers/undefined [APICC-3238].
    getSanitizedConversationId() {
      const value = this.$route?.query?.conversation;
      if (!value || value === 'undefined' || value === 'null') {
        return undefined;
      }
      return value;
    },
    changeActiveChat(customer) {
      this.activeChatId = customer;
      this.$router.replace({ path: '/page-inbox', query: { conversation: this.activeChatId } });
    },
    goToHome() {
      this.activeChatId = undefined;
      this.active = "home";
      this.$router.replace({ path: '/page-inbox' });
    },
    async startNewConv() {
      try {
        if(this.addEditModal && this.newChat.channel) {
          this.addEditModal = false;
          return;
        }
        this.commonLoader = true;
        const { firstName, lastName, mobilenumber, channel, countryCode } = this.newChat;
        //if (!firstName || firstName.length === 0) return useUIStore().updateToast({ open: true, message: "Please enter the first name", type: "warning" });
        //if (!lastName || lastName.length === 0) return useUIStore().updateToast({ open: true, message: "Please enter the last name", type: "warning" });
        if (!mobilenumber || mobilenumber.length === 0) return useUIStore().updateToast({ open: true, message: "Please enter a valid mobile number", type: "warning" });

        const createRequestBody = (pageid) => ({
          conversationid: countryCode.replace('+', '') + mobilenumber,
          channel,
          userdata: {
            country_code: countryCode,
            mobile_number: mobilenumber,
            first_name: firstName,
            last_name: lastName,
          },
          pageid: pageid.toString()
        });

        const channelDataMap = {
          sms: this.smsSocialData?.id,
          googlercs: this.googlercsSocialData?.id,
          whatsapp: this.whatsAppSocialData?.id,
        };

        const pageId = channelDataMap[channel];
        if (!pageId) throw new Error("Invalid channel selected");

        const reqBody = createRequestBody(pageId);
        const response = await apiInterceptor.post(`${environment("VUE_APP_API_URL")}crm/v1/customers/`, reqBody);
        useUIStore().updateToast({ open: true, message: 'Customer has been added to CRM successfully', type: "success" });
        this.active = "chat";
        this.changeActiveChat(response.conversationid);
        this.newChat = JSON.parse(JSON.stringify(newChat));
        this.commonLoader = false;
      } catch (error) {
        console.error("Exception: ", error);
        useUIStore().updateToast({ open: true, message: error.message, type: "danger" });
      }
    },
    onresize(e) {
      let width = e?.target?.innerWidth || window.innerWidth;
      if(width > 1200) this.isMobile = false;
      else this.isMobile = true;
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onresize);
  },
};
</script>

<style scoped lang="scss">
.page-inbox-container {
  height: calc(100vh - 60px); /* Full viewport minus header height */
  overflow: hidden;
}
.inbox {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.col-home {
  width: 25%;
  min-width: 300px;
  max-width: 380px;
  height: 100%;
  background: var(--white);
  border-right: 1px solid var(--background-primary);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.col-chat {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.col-full {
  width: 100%;
  height: calc(100vh - 60px);
  overflow: hidden;
  background: var(--white);
  display: flex;
  flex-direction: column;
}
.start-conversation {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.channel-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--background-primary);
  border-radius: 5px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border-color 0.2s;
}
.channel-item.selected {
  border-color: var(--purple);
}
.channel-item .label {
  display: flex;
  align-items: center;
  gap: 10px;
}
.channel-icon-img {
  width: 24px;
  height: 24px;
}
.radio-indicator {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--gray-04);
  transition: all 0.2s;
}
.radio-indicator.active {
  border-color: var(--purple);
  background: var(--purple);
  box-shadow: inset 0 0 0 3px var(--white);
}
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
.footer {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
}
.channel_container{
  padding: 0px 20px 0px 20px
}

@media only screen and (max-width: 1200px) {
  .col-home {
    width: 35%;
    min-width: 280px;
    max-width: 400px;
  }
}
@media only screen and (max-width: 992px) {
  .col-home {
    width: 40%;
    min-width: 260px;
    max-width: 350px;
  }
}
</style>
